import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Calculator() {
  const [current, setCurrent] = useState('200');
  const [goal, setGoal] = useState('160');
  const [months, setMonths] = useState('6');
  const [result, setResult] = useState(null);

  const handleCalculate = (e) => {
    e.preventDefault();
    const diff = Number(current) - Number(goal);
    if (diff <= 0) {
      setResult({ type: 'error', text: "Your goal weight should be less than your current weight." });
      return;
    }
    const perMonth = (diff / Number(months)).toFixed(1);
    setResult({ 
      type: 'success', 
      text: `To reach your goal, you'll need to lose approximately`,
      highlight: `${perMonth} lbs per month`,
      subtext: `Our treatments are designed to help you achieve these sustainable results!`
    });
  };

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Abstract background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-teal-900/20 blur-3xl rounded-full transform translate-x-1/3 -translate-y-1/4"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-full bg-emerald-900/20 blur-3xl rounded-full transform -translate-x-1/3 translate-y-1/4"></div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-6">How much could you lose?</h2>
          <p className="text-xl text-teal-100/80 leading-relaxed">Calculate your potential weight loss trajectory with our medically-supported treatments.</p>
        </div>

        <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-8 md:p-12 shadow-2xl">
          <form onSubmit={handleCalculate} className="grid md:grid-cols-3 gap-8">
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-teal-100 tracking-wide uppercase">Current Weight (lbs)</label>
              <input 
                type="number" 
                value={current} 
                onChange={(e) => setCurrent(e.target.value)} 
                className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all placeholder-white/30" 
                required 
              />
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-teal-100 tracking-wide uppercase">Goal Weight (lbs)</label>
              <input 
                type="number" 
                value={goal} 
                onChange={(e) => setGoal(e.target.value)} 
                className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all placeholder-white/30" 
                required 
              />
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-teal-100 tracking-wide uppercase">Timeframe</label>
              <select 
                value={months} 
                onChange={(e) => setMonths(e.target.value)} 
                className="w-full px-5 py-4 rounded-2xl bg-white/5 border border-white/10 text-white text-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all appearance-none [&>option]:text-slate-900"
              >
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">12 months</option>
              </select>
            </div>
            <div className="md:col-span-3 mt-4">
              <button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-slate-900 text-lg font-bold py-5 rounded-2xl transition-all shadow-lg hover:shadow-teal-500/25">
                Calculate Projection
              </button>
            </div>
          </form>

          <AnimatePresence>
            {result && (
              <motion.div 
                initial={{ opacity: 0, height: 0, marginTop: 0 }} 
                animate={{ opacity: 1, height: 'auto', marginTop: 32 }} 
                exit={{ opacity: 0, height: 0, marginTop: 0 }}
                className="overflow-hidden"
              >
                <div className={`p-8 rounded-2xl border ${result.type === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-200' : 'bg-teal-500/10 border-teal-500/20 text-teal-50'} text-center`}>
                  {result.type === 'error' ? (
                    <p className="font-semibold text-lg">{result.text}</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-lg opacity-90">{result.text}</p>
                      <p className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-emerald-300">{result.highlight}</p>
                      <p className="text-sm opacity-70 pt-2">{result.subtext}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}